import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Clock, BarChart3, CheckCircle2, ChevronRight, MessageCircle } from "lucide-react";
import api, { waLink } from "../lib/api";
import { Reveal, Eyebrow } from "../components/Reveal";
import { EnquiryForm } from "../components/EnquiryModal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { useTitle } from "../hooks/useTitle";

export default function CourseDetail() {
  const { slug } = useParams();
  const [course, setCourse] = useState(null);
  const [notFound, setNotFound] = useState(false);
  useTitle(course ? course.title : "Course");

  useEffect(() => {
    setCourse(null);
    setNotFound(false);
    api.get(`/courses/${slug}`).then((r) => setCourse(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound)
    return (
      <div className="py-32 text-center" data-testid="course-not-found">
        <h1 className="font-display font-extrabold text-2xl text-[#0A192F]">Course not found</h1>
        <Link to="/courses" className="text-[#0F5132] font-bold text-sm mt-4 inline-block">← Back to all courses</Link>
      </div>
    );
  if (!course) return <div className="py-32 text-center text-sm text-[#627D98]">Loading course...</div>;

  return (
    <div data-testid="course-detail-page">
      <section className="bg-[#0A192F] relative overflow-hidden py-14 lg:py-20">
        <div className="absolute inset-0 hero-grid" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center gap-1.5 text-xs text-slate-400 mb-6" data-testid="course-breadcrumbs">
            <Link to="/" className="hover:text-[#C59B27]">Home</Link>
            <ChevronRight size={12} />
            <Link to="/courses" className="hover:text-[#C59B27]">Courses</Link>
            <ChevronRight size={12} />
            <span className="text-[#C59B27]">{course.title}</span>
          </nav>
          {course.badge && (
            <span className="inline-block px-3 py-1 rounded-full bg-[#C59B27]/15 border border-[#C59B27]/40 text-[#C59B27] text-[10px] font-mono-sn font-bold tracking-widest mb-4">
              {course.badge}
            </span>
          )}
          <h1 className="font-display font-extrabold tracking-tight text-white text-3xl sm:text-4xl lg:text-5xl max-w-3xl leading-[1.1]">
            {course.title}
          </h1>
          <p className="mt-5 text-slate-300 text-base max-w-2xl leading-relaxed">{course.short_desc}</p>
          <div className="mt-7 flex flex-wrap gap-4 text-sm font-semibold text-slate-200">
            <span className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/15">
              <Clock size={14} className="text-[#C59B27]" /> {course.duration}
            </span>
            <span className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/15">
              <BarChart3 size={14} className="text-[#C59B27]" /> {course.level}
            </span>
          </div>
        </div>
      </section>

      <section className="py-14 lg:py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-[1fr_380px] gap-12">
        <div>
          {course.image_url && (
            <Reveal>
              <img src={course.image_url} alt={course.title} className="w-full h-64 sm:h-80 object-cover rounded-2xl border border-slate-200" />
            </Reveal>
          )}
          <Reveal className="mt-10">
            <Eyebrow>About this course</Eyebrow>
            <p className="mt-4 text-base text-[#334E68] leading-relaxed">{course.description}</p>
          </Reveal>
          <Reveal className="mt-10">
            <Eyebrow>What you will learn</Eyebrow>
            <div className="mt-5 grid sm:grid-cols-2 gap-3" data-testid="course-modules">
              {(course.modules || []).map((m, i) => (
                <div key={i} className="flex items-start gap-2.5 bg-white border border-slate-200 rounded-xl p-4">
                  <CheckCircle2 size={16} className="text-[#0F5132] shrink-0 mt-0.5" />
                  <span className="text-sm font-semibold text-[#334E68]">{m}</span>
                </div>
              ))}
            </div>
          </Reveal>
          {course.faqs && course.faqs.length > 0 && (
            <Reveal className="mt-10">
              <Eyebrow>Course FAQs</Eyebrow>
              <Accordion type="single" collapsible className="mt-5 space-y-3">
                {course.faqs.map((f, i) => (
                  <AccordionItem key={i} value={`cf-${i}`} className="bg-white border border-slate-200 rounded-xl px-5">
                    <AccordionTrigger className="font-display font-bold text-sm text-[#0A192F] text-left hover:no-underline">
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-[#486581]">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Reveal>
          )}
        </div>
        <aside className="lg:sticky lg:top-24 self-start space-y-5">
          <div className="bg-white rounded-2xl border-2 border-[#C59B27]/60 p-6 shadow-lg" data-testid="course-enquiry-card">
            <h3 className="font-display font-extrabold text-lg text-[#0A192F]">Get Course Details</h3>
            <p className="text-xs text-[#627D98] mt-1 mb-5">Syllabus, batch timings & fees — straight to your phone.</p>
            <EnquiryForm defaultInterest={course.title} source={`course-page-${slug}`} />
          </div>
          <a
            href={waLink(`Hi, I am interested in the ${course.title} course. Please share the details.`)}
            target="_blank"
            rel="noreferrer"
            data-testid="course-whatsapp-btn"
            className="flex items-center justify-center gap-2 w-full py-3.5 rounded-xl bg-[#25D366] text-white font-display font-bold text-sm hover:bg-[#1fb857] transition-colors"
          >
            <MessageCircle size={17} /> Ask on WhatsApp
          </a>
        </aside>
      </section>
    </div>
  );
}
